import { useEffect, useMemo, useRef, useState } from 'react'
import { parseItemSalesCsv } from '../lib/csvParser.js'
import { formatMoney } from '../lib/format.js'

// Modal for importing per-item sales from a Toast Item Sales CSV.
//
// Powers the Theoretical-vs-Actual variance feature: each row says
// "this menu item sold N units for $M in net sales over the period
// you exported." We multiply N by recipe components to derive the
// theoretical inventory depletion for that period.
//
// Flow: pick file → auto-parse + match-against-catalog preview →
// pick period start/end (Toast doesn't export the date range you
// chose at export time, so the manager confirms it here) → confirm.
// Idempotent: re-importing the same period for the same item
// upserts.
export default function ItemSalesImportModal({
  open,
  onClose,
  onImport,        // ({ rows, period_start, period_end }) → Promise<count>
  existingItems,   // [{ id, name, toast_guid }]
}) {
  const fileRef = useRef(null)
  const [filename, setFilename]     = useState('')
  const [parsed, setParsed]         = useState(null)
  const [periodStart, setPeriodStart] = useState('')
  const [periodEnd, setPeriodEnd]   = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError]           = useState(null)

  // Reset on open. Default the period to "last 7 days" so the
  // manager's most-likely export window is pre-filled.
  useEffect(() => {
    if (!open) return
    setFilename('')
    setParsed(null)
    setError(null)
    setSubmitting(false)
    if (fileRef.current) fileRef.current.value = ''
    const today = new Date()
    const end   = ymd(today)
    const start = ymd(new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000))
    setPeriodStart(start)
    setPeriodEnd(end)
  }, [open])

  // Build a name + toast_guid lookup once, used to compute match
  // counts for the preview card.
  const itemsLookup = useMemo(() => {
    const byName = new Map()
    const byGuid = new Map()
    for (const i of existingItems ?? []) {
      byName.set((i.name || '').toLowerCase().trim(), i)
      if (i.toast_guid) byGuid.set(i.toast_guid, i)
    }
    return { byName, byGuid }
  }, [existingItems])

  // Match each parsed row to an item in the catalog (by guid first,
  // name second). The actual save happens in useSettingsData so the
  // logic is duplicated here only for the preview UI.
  const matchSummary = useMemo(() => {
    const rows = parsed?.rows ?? []
    let matched = 0
    let unmatched = 0
    let totalQty = 0
    let totalNet = 0
    for (const r of rows) {
      const m = (r.toast_guid && itemsLookup.byGuid.get(r.toast_guid)) ||
                itemsLookup.byName.get((r.item_name || '').toLowerCase().trim())
      if (m) matched++
      else unmatched++
      totalQty += Number(r.quantity_sold || 0)
      totalNet += Number(r.net_sales || 0)
    }
    return { matched, unmatched, totalQty, totalNet, count: rows.length }
  }, [parsed, itemsLookup])

  if (!open) return null

  async function handleFile(e) {
    const file = e.target.files?.[0]
    if (!file) return
    setFilename(file.name)
    setError(null)
    try {
      const text = await file.text()
      const result = parseItemSalesCsv(text)
      setParsed(result)
      if (result.rows.length === 0) {
        setError(result.errors[0] ?? 'No usable rows found in this CSV.')
      }
    } catch (e2) {
      // eslint-disable-next-line no-console
      console.error('[ItemSalesImportModal] parse failed', e2)
      setError('Could not read this file. Make sure it’s a CSV export.')
    }
  }

  async function handleConfirm() {
    if (submitting || !parsed?.rows?.length) return
    if (!periodStart || !periodEnd) {
      setError('Pick a period start + end before importing.')
      return
    }
    if (periodEnd < periodStart) {
      setError('Period end must be on or after period start.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      await onImport({
        rows: parsed.rows,
        period_start: periodStart,
        period_end:   periodEnd,
      })
      onClose?.()
    } catch (e2) {
      // eslint-disable-next-line no-console
      console.error('[ItemSalesImportModal] import failed', e2)
      setError('Could not save to the database. Try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-40">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 animate-fade-in"
      />

      <div
        className="animate-sheet-up absolute inset-x-0 bottom-0 max-h-[92vh] overflow-y-auto rounded-t-2xl bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label="Import item sales CSV"
      >
        <div className="sticky top-0 z-10 bg-white pt-2">
          <div className="mx-auto h-1.5 w-12 rounded-full bg-black/15" />
          <div className="flex items-center justify-between px-4 py-3">
            <h2 className="text-xl font-medium">Import item sales</h2>
            <button
              type="button"
              onClick={onClose}
              className="min-h-btn min-w-[48px] rounded-lg px-3 text-base font-medium text-black/70 hover:bg-black/5"
              aria-label="Cancel"
            >
              Cancel
            </button>
          </div>
          {error && (
            <div role="alert" className="mx-4 mb-2 rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white shadow">
              {error}
            </div>
          )}
        </div>

        <div className="space-y-4 px-4 pb-4">
          <div className="rounded-xl border border-blue-200 bg-blue-50/40 p-3 text-sm leading-relaxed text-blue-900/90">
            <div className="mb-1 font-medium">From Toast Web:</div>
            Reports → <span className="font-medium">Item Sales</span> →
            choose your date range → <span className="font-medium">by Item</span> aggregation →
            <span className="font-medium"> Export CSV</span>.
            <div className="mt-2 text-xs text-blue-900/70">
              Powers the Theoretical-vs-Actual variance report. Items are
              matched to your catalog by name (or Toast GUID if present).
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="item-sales-file">
              Choose CSV file
            </label>
            <input
              ref={fileRef}
              id="item-sales-file"
              type="file"
              accept=".csv,text/csv"
              onChange={handleFile}
              className="block w-full rounded-lg border border-black/15 px-3 py-3 text-sm file:mr-3 file:rounded-md file:border-0 file:bg-blue-600 file:px-3 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-blue-700"
            />
            {filename && (
              <p className="mt-1 truncate text-xs text-black/55">
                Selected: <span className="font-medium text-black/80">{filename}</span>
              </p>
            )}
          </div>

          {/* Period picker — Toast's CSV doesn't carry the export date
              range, so we ask the manager to confirm it here. */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="period-start">
                Period start
              </label>
              <input
                id="period-start"
                type="date"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
                className="w-full rounded-lg border border-black/15 px-4 py-3 text-base font-medium tabular-nums outline-none focus:border-blue-600"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="period-end">
                Period end
              </label>
              <input
                id="period-end"
                type="date"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
                className="w-full rounded-lg border border-black/15 px-4 py-3 text-base font-medium tabular-nums outline-none focus:border-blue-600"
              />
            </div>
          </div>

          {parsed?.headers?.length > 0 && (
            <div className="rounded-xl border border-black/10 bg-black/[0.02] p-3 text-xs">
              <div className="mb-1 font-medium text-black/60">
                Detected columns
              </div>
              <div className="space-y-0.5">
                <DetectedRow label="Item name"     idx={parsed.columns?.nameIdx}     headers={parsed.headers} />
                <DetectedRow label="Quantity sold" idx={parsed.columns?.qtyIdx}      headers={parsed.headers} />
                <DetectedRow label="Net sales"     idx={parsed.columns?.netSalesIdx} headers={parsed.headers} optional />
                <DetectedRow label="Gross sales"   idx={parsed.columns?.grossIdx}    headers={parsed.headers} optional />
                <DetectedRow label="Toast GUID"    idx={parsed.columns?.guidIdx}     headers={parsed.headers} optional />
              </div>
            </div>
          )}

          {parsed?.rows?.length > 0 && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4">
              <div className="mb-1 text-xs font-medium text-emerald-900/70">
                Preview
              </div>
              <div className="text-sm text-emerald-900">
                <span className="font-medium tabular-nums">{matchSummary.count}</span>{' '}
                {matchSummary.count === 1 ? 'item' : 'items'} ·{' '}
                <span className="font-medium tabular-nums">{matchSummary.totalQty}</span>{' '}
                units sold ·{' '}
                <span className="font-medium tabular-nums">{formatMoney(matchSummary.totalNet)}</span>{' '}
                net
              </div>
              <div className="mt-2 grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-md bg-emerald-100/60 px-3 py-2">
                  <div className="text-xs text-emerald-900/70">Matched to catalog</div>
                  <div className="text-lg font-medium tabular-nums text-emerald-900">
                    {matchSummary.matched}
                  </div>
                </div>
                <div className={`rounded-md px-3 py-2 ${
                  matchSummary.unmatched > 0
                    ? 'bg-amber-100/60'
                    : 'bg-emerald-100/40'
                }`}>
                  <div className={`text-xs ${
                    matchSummary.unmatched > 0
                      ? 'text-amber-900/80'
                      : 'text-emerald-900/70'
                  }`}>Unmatched</div>
                  <div className={`text-lg font-medium tabular-nums ${
                    matchSummary.unmatched > 0
                      ? 'text-amber-900'
                      : 'text-emerald-900/60'
                  }`}>
                    {matchSummary.unmatched}
                  </div>
                </div>
              </div>
              {matchSummary.unmatched > 0 && (
                <p className="mt-2 text-xs leading-snug text-amber-900/85">
                  Unmatched rows still get saved with their item names —
                  variance reporting will skip them until you create or
                  rename matching items in your catalog.
                </p>
              )}
            </div>
          )}

          {parsed?.errors?.length > 0 && parsed.rows.length > 0 && (
            <details className="rounded-xl border border-amber-200 bg-amber-50/40 p-3 text-xs text-amber-900">
              <summary className="cursor-pointer font-medium">
                {parsed.errors.length} row{parsed.errors.length === 1 ? '' : 's'} skipped — click to review
              </summary>
              <ul className="mt-2 space-y-1">
                {parsed.errors.slice(0, 20).map((msg, idx) => (
                  <li key={idx} className="leading-snug">{msg}</li>
                ))}
                {parsed.errors.length > 20 && (
                  <li className="text-amber-900/70">…and {parsed.errors.length - 20} more</li>
                )}
              </ul>
            </details>
          )}

          {!parsed && !error && (
            <p className="text-xs text-black/55">
              Re-importing the same period overwrites the previous import for
              that period, so you can refine your export and re-upload safely.
            </p>
          )}
        </div>

        <div className="sticky bottom-0 z-10 border-t border-black/5 bg-white px-4 pt-3 safe-bottom">
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting || !parsed?.rows?.length || !periodStart || !periodEnd}
            className="min-h-btn w-full rounded-lg bg-blue-600 px-4 py-4 text-xl font-medium text-white shadow-sm hover:bg-blue-700 active:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting
              ? 'Importing\u2026'
              : parsed?.rows?.length
                ? `Import ${parsed.rows.length} ${parsed.rows.length === 1 ? 'item' : 'items'} for ${periodStart} \u2192 ${periodEnd}`
                : 'Pick a CSV first'}
          </button>
        </div>
      </div>
    </div>
  )
}

function DetectedRow({ label, idx, headers, optional }) {
  const found = idx != null && idx >= 0
  return (
    <div className="flex justify-between gap-2">
      <span className="text-black/60">{label}{optional ? ' (optional)' : ''}</span>
      <span className={`truncate font-medium ${
        found ? 'text-emerald-700' : (optional ? 'text-black/40' : 'text-red-600')
      }`}>
        {found ? headers[idx] : (optional ? '—' : 'not found')}
      </span>
    </div>
  )
}

// Local helper — formats a Date as YYYY-MM-DD in the browser's
// timezone (so the period picker matches what the manager expects
// on their phone).
function ymd(d) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}
